/** Center-origin box in canvas pixels, angle in degrees */
export interface PrintAreaBox {
    left: number;
    top: number;
    width: number;
    height: number;
    angle: number;
}

/**
 * Same naming as the selection toolbar's canvas alignment: "middle" centers horizontally and "center"
 *  centers vertically
 */
export type PrintAreaAction = 'fit' | 'top' | 'bottom' | 'left' | 'right' | 'middle' | 'center';

const rotate = (x: number, y: number, degrees: number) => {
    const radians = (degrees * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    return { x: x * cos - y * sin, y: x * sin + y * cos };
};

/**
 * Box the design takes after fitting or aligning it inside the print area's bounding box. The design keeps
 *  its aspect ratio and angle.
 */
export const getDesignBoxForPrintArea = (
    printArea: PrintAreaBox,
    design: PrintAreaBox,
    action: PrintAreaAction
): PrintAreaBox => {
    // Worked out in the print area's own axes so a rotated print area aligns along its edges, not the canvas'
    const local = rotate(design.left - printArea.left, design.top - printArea.top, -printArea.angle);

    // Footprint along those axes: only differs from width/height when the design is rotated against the print area
    const { x: cos, y: sin } = rotate(1, 0, design.angle - printArea.angle);
    let { width, height } = design;
    const footprintWidth = Math.abs(width * cos) + Math.abs(height * sin);
    const footprintHeight = Math.abs(width * sin) + Math.abs(height * cos);

    switch (action) {
        case 'fit': {
            const scale = Math.min(printArea.width / footprintWidth, printArea.height / footprintHeight);
            width *= scale;
            height *= scale;
            local.x = 0;
            local.y = 0;
            break;
        }
        case 'top':
            local.y = -(printArea.height - footprintHeight) / 2;
            break;
        case 'bottom':
            local.y = (printArea.height - footprintHeight) / 2;
            break;
        case 'left':
            local.x = -(printArea.width - footprintWidth) / 2;
            break;
        case 'right':
            local.x = (printArea.width - footprintWidth) / 2;
            break;
        case 'middle':
            local.x = 0;
            break;
        case 'center':
            local.y = 0;
            break;
    }

    const offset = rotate(local.x, local.y, printArea.angle);
    return {
        left: printArea.left + offset.x,
        top: printArea.top + offset.y,
        width,
        height,
        angle: design.angle
    };
};
